import { ArdoqId, Entity } from '@ardoq/api-types';
import { toIds } from '@ardoq/core';
import { withOnFirstSubscription } from '@ardoq/common-helpers';
import {
  Action,
  ActionCreator,
  ActionCreatorWithoutNamespace,
  action$,
  actionCreator,
  connect,
  dispatchAction,
  extractPayload,
  ofType,
  withNamespace,
} from '@ardoq/rxbeach';
import { debounce, keyBy } from 'lodash';
import {
  Observable,
  ObservableInput,
  Subscriber,
  Subscription,
  combineLatest,
  firstValueFrom,
} from 'rxjs';
import { tag } from 'rxjs-spy/operators';
import { debounceTime, first, shareReplay } from 'rxjs/operators';

export const toStreamCollection = <T extends Entity>(
  models: T[]
): StreamCollection<T> => ({
  byId: keyBy(models, '_id'),
  sortIds: toIds(models),
  models,
});

const serializeCollection = (backboneObj: Backbone.Collection<any>) => {
  const models = backboneObj.map(m => m.attributes);

  return {
    byId: keyBy(models, '_id'),
    sortIds: toIds(models),
    models,
  };
};

/**
 * Be aware that when emitting this event, no debouncing is applied.
 * Also, don't really use this at all. It's a hack.
 */
export const forceUpdateStream = '_forceUpdateStream';

const createObservableFromBackbone = <T>(
  backboneObj: Backbone.Model | Backbone.Collection<any>,
  serializer: (x: any) => any
): Observable<T> =>
  Observable.create((observer: Subscriber<T>) => {
    observer.next(serializer(backboneObj));
    backboneObj.on(
      'sync destroy remove add',
      debounce(
        () => observer.next(serializer(backboneObj)),
        48 /* ~ three frames */,
        {
          leading: false,
          trailing: true,
        }
      ),
      observer
    );
    backboneObj.on(forceUpdateStream, () => {
      observer.next(serializer(backboneObj));
    });

    return () => backboneObj.off(undefined, undefined, observer);
  }).pipe(
    tag(`collection$-bb-${backboneObj.url}`),
    shareReplay({ bufferSize: 1, refCount: true })
  );

export const createStreamFromBackboneModel = <T extends Entity>(
  model: Backbone.Model
): Observable<T> => {
  return createObservableFromBackbone(model, model => model.attributes);
};

export type StreamCollection<T extends Entity> = {
  byId: Record<ArdoqId, T>;
  sortIds: string[];
  models: T[];
};

export const getStreamCollectionInitialState = <
  T extends Entity,
>(): StreamCollection<T> => ({
  byId: {},
  sortIds: [],
  models: [],
});

// if attributes are not passed, we will return _all_ of the attributes of the entity.
export const createStreamFromBackboneCollection = <T extends Entity>(
  collection: Backbone.Collection<any>
): Observable<StreamCollection<T>> => {
  return createObservableFromBackbone<StreamCollection<T>>(
    collection,
    collection => serializeCollection(collection)
  );
};

const getNamespaceStream = (namespace?: string) =>
  namespace ? action$.pipe(withNamespace(namespace)) : action$;

export const subscribeToAction = <T>(
  action: ActionCreator<T>,
  handler: (payload: T) => void,
  namespace?: string
): Subscription =>
  getNamespaceStream(namespace)
    .pipe(ofType(action))
    .subscribe((action: Action<T>) => {
      handler(action.payload);
    });

export const onFirstAction = <Payload>(
  action: ActionCreator<Payload>,
  func: (payload: Payload) => void,
  namespace?: string
) =>
  getNamespaceStream(namespace)
    .pipe(ofType(action), extractPayload(), first())
    .subscribe(func);

export const subscribeToActionOnce = <T>(
  action: ActionCreator<T>,
  handler: (payload?: T) => void,
  namespace?: string
) => {
  const wrappedHandler = (payload?: T) => {
    subscription.unsubscribe();
    handler(payload);
  };
  const subscription = subscribeToAction(action, wrappedHandler, namespace);
};

export const clearSubscriptions = (subscriptions: Subscription[]) => {
  subscriptions.forEach(subscription => subscription.unsubscribe());
  return [];
};

type DebouncedCombineLatest = {
  <A>(dependencies: [ObservableInput<A>], time?: number): Observable<[A]>;
  <A, B>(
    dependencies: [ObservableInput<A>, ObservableInput<B>],
    time?: number
  ): Observable<[A, B]>;
  <A, B, C>(
    dependencies: [ObservableInput<A>, ObservableInput<B>, ObservableInput<C>],
    time?: number
  ): Observable<[A, B, C]>;
  <A, B, C, D>(
    dependencies: [
      ObservableInput<A>,
      ObservableInput<B>,
      ObservableInput<C>,
      ObservableInput<D>,
    ],
    time?: number
  ): Observable<[A, B, C, D]>;
  <A, B, C, D, E>(
    dependencies: [
      ObservableInput<A>,
      ObservableInput<B>,
      ObservableInput<C>,
      ObservableInput<D>,
      ObservableInput<E>,
    ],
    time?: number
  ): Observable<[A, B, C, D, E]>;
  <A, B, C, D, E, F>(
    dependencies: [
      ObservableInput<A>,
      ObservableInput<B>,
      ObservableInput<C>,
      ObservableInput<D>,
      ObservableInput<E>,
      ObservableInput<F>,
    ],
    time?: number
  ): Observable<[A, B, C, D, E, F]>;
  <A, B, C, D, E, F, G>(
    dependencies: [
      ObservableInput<A>,
      ObservableInput<B>,
      ObservableInput<C>,
      ObservableInput<D>,
      ObservableInput<E>,
      ObservableInput<F>,
      ObservableInput<G>,
    ],
    time?: number
  ): Observable<[A, B, C, D, E, F, G]>;
  <A, B, C, D, E, F, G, H>(
    dependencies: [
      ObservableInput<A>,
      ObservableInput<B>,
      ObservableInput<C>,
      ObservableInput<D>,
      ObservableInput<E>,
      ObservableInput<F>,
      ObservableInput<G>,
      ObservableInput<H>,
    ],
    time?: number
  ): Observable<[A, B, C, D, E, F, G, H]>;
  <A, B, C, D, E, F, G, H, I>(
    dependencies: [
      ObservableInput<A>,
      ObservableInput<B>,
      ObservableInput<C>,
      ObservableInput<D>,
      ObservableInput<E>,
      ObservableInput<F>,
      ObservableInput<G>,
      ObservableInput<H>,
      ObservableInput<I>,
    ],
    time?: number
  ): Observable<[A, B, C, D, E, F, G, H, I]>;
  <A, B, C, D, E, F, G, H, I, J>(
    dependencies: [
      ObservableInput<A>,
      ObservableInput<B>,
      ObservableInput<C>,
      ObservableInput<D>,
      ObservableInput<E>,
      ObservableInput<F>,
      ObservableInput<G>,
      ObservableInput<H>,
      ObservableInput<I>,
      ObservableInput<J>,
    ],
    time?: number
  ): Observable<[A, B, C, D, E, F, G, H, I, J]>;
  (
    dependencies: ObservableInput<unknown>[],
    time?: number
  ): Observable<unknown>;
};

/**
 * Combine the input streams and debounce their emission.
 * The method can be used to prevent glitches / redundant calculations.
 *
 * @param dependencies The dependencies of this stream
 * @param time Number of milliseconds to debounce, defaults to 0
 * @see combineLatest
 */
export const debouncedCombineLatest: DebouncedCombineLatest = (
  dependencies: ObservableInput<any>[],
  time = 0
): Observable<any> => combineLatest(dependencies).pipe(debounceTime(time));

export const getActionNamespacer =
  (namespace: string) =>
  (label: string): Parameters<typeof actionCreator>[0] =>
    `[${namespace}] ${label}`;

export const lazyConnect = <
  Props extends object,
  Observed extends Partial<Props>,
>(
  reactComponent: React.ComponentType<Props>,
  stream: Observable<Observed>,
  action: ActionCreatorWithoutNamespace
) =>
  connect<Props, Observed>(
    reactComponent,
    withOnFirstSubscription(stream, () => dispatchAction(action()))
  );

export type CollectionStream<T> = {
  list: T[];
  byId: Record<ArdoqId, T>;
};
/**
 * Updates each existing entry whose `_id` also appears in `entities`,
 * keeping its position, and appends the rest of `entities` as new. Callers
 * that slice/paginate the returned array by position rely on this.
 */
export const upsertComponents = <T extends { _id: ArdoqId }>(
  stateEntities: T[],
  incomingEntities: T[]
): T[] => {
  const incomingById = new Map(incomingEntities.map(e => [e._id, e]));
  const merged = stateEntities.map(stateEntity => {
    const incomingEntity = incomingById.get(stateEntity._id);
    if (!incomingEntity) return stateEntity;
    incomingById.delete(stateEntity._id);
    return { ...stateEntity, ...incomingEntity };
  });
  return [...merged, ...incomingById.values()];
};

export const toCollectionStream = <T>(list: T[]): CollectionStream<T> => ({
  byId: keyBy(list, '_id'),
  list,
});

export const emptyCollectionStream = <T>(): CollectionStream<T> => ({
  byId: {},
  list: [],
});

/**
 * @deprecated This is an anti-pattern that blocks the event loop by awaiting actions.
 * We should react to downstream changes in streams instead of imperatively waiting.
 * This function exists to make the anti-pattern explicit and searchable.
 *
 * DO NOT use this in new code. Instead, handle state changes reactively in routines
 * or by subscribing to the relevant state streams.
 *
 * If you must use this, document why the imperative approach is necessary in your
 * specific case (e.g., legacy code that requires async/await patterns throughout).
 *
 * @example
 * // BAD - Blocking the event loop
 * await DEPRECATED_waitForAction(someAction);
 * doSomething();
 *
 * // GOOD - Reactive approach
 * routine(
 *   ofType(someAction),
 *   tap(() => doSomething())
 * );
 */
export const DEPRECATED_waitForAction = <T>(
  ...actions: ActionCreator<T>[]
): Promise<Action<T>> => firstValueFrom(action$.pipe(ofType(...actions)));
